/**
 * ==================== 视频图层模块 - AI 视频生成全链路 ====================
 *
 * 职责：合并原 videoTask.ts（任务执行）和 canvasVideoTask.ts（参数构建），
 * 形成完整的 AI 视频生成 → 图层创建链路。
 *
 * 依赖：./factory（createVideoLayerConfig）、./types（CanvasVideoMode 等）
 * 被依赖：index.ts
 */

import { ModuleId } from '@xmiles/holopix-types'
import { getModuleLabelById } from '@xmiles/holopix-helper'
import { useTaskEventStore } from '../store/taskEvent'
import { useUserStore } from '@/store/user'
import { useCanvasEditor } from '../../hooks/useCanvasEditor'
import {
  createGenerationTaskStarter,
  bridgeTaskProgressToStore,
  bridgeTaskErrorToStore,
} from '../generationTaskBridge'
import { createVideoLayerConfig } from './factory'
import {
  getVideoGenerateModuleIdWithAssets,
  resolveVideoModeByModuleId,
  resolveVideoModelKeyByModuleId,
  getVideoApiModelName,
  mapApiModelToVideoModelKey,
  videoQualityToResolution,
  resolveVideoResolutionParam,
  isMinimaxStringResolutionModuleId,
  type VideoModelKey,
  type VideoQuality,
} from '@/views/workspace/board/list/components/AdvancedHeroActions/videoGenerationConfig'
import { resolveGenerationSimulatorUserTier } from '../../domain/generation/simulatorTier'
import { resolveGenerationLayoutSourceKind } from '../../domain/generationLayout/sourceKind'
import { extractOriginalUrl } from '../optimization'
// 参考图纯逻辑模块（可独立单测）：仅保留对外 re-export，保持 index.ts API 不变
import { sanitizeUrl, resolveCanvasVideoMode, resolveCanvasVideoReferenceCollections } from './reference'
import type {
  CanvasVideoMode,
  CanvasVideoReferenceType,
  CanvasVideoReferenceItem,
  CanvasVideoPanelPayload,
} from './types'

// ========== 类型重导出（供外部消费方使用）==========

export type {
  CanvasVideoMode,
  CanvasVideoReferenceType,
  CanvasVideoReferenceItem,
  CanvasVideoPanelPayload,
}

// ========== 来自原 videoTask.ts ==========

/**
 * 规范化视频生成完成结果
 *
 * 兼容多种后端返回格式，统一提取 images、videoUrl、coverUrl
 */
export const normalizeVideoCompleteResult = (result: any) => {
  const rawList = Array.isArray(result)
    ? result
    : Array.isArray(result?.images)
      ? result.images
      : Array.isArray(result?.data?.images)
        ? result.data.images
        : result
          ? [result]
          : []

  const normalizedImages = rawList
    .map((item: any) => {
      const imageUrl =
        item?.coverUrl ||
        item?.cover_url ||
        item?.image ||
        item?.url ||
        item?.videoUrl ||
        item?.video ||
        ''
      if (!imageUrl) return null
      return {
        image: imageUrl,
        width: Number(item?.width) || 0,
        height: Number(item?.height) || 0,
        id: item?.id ?? item?.resultId,
      }
    })
    .filter(Boolean) as Array<{
    image: string
    width: number
    height: number
    id?: number | string
  }>

  const firstRaw = rawList[0] || {}
  const videoUrl =
    firstRaw?.videoUrl ||
    firstRaw?.video ||
    result?.videoUrl ||
    result?.video ||
    normalizedImages[0]?.image ||
    ''

  const coverUrl =
    firstRaw?.coverUrl ||
    firstRaw?.cover_url ||
    result?.coverUrl ||
    result?.cover_url ||
    ''

  return { normalizedImages, videoUrl, coverUrl }
}

let _taskManager: any = null

/**
 * 初始化视频任务依赖
 *
 * 在画布初始化时调用，配置任务管理器实例
 */
export const initVideoTaskDeps = () => {
  _taskManager = createGenerationTaskStarter()
}

/**
 * 规范化视频分辨率参数
 */
const normalizeVideoResolution = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0)
    return Math.round(value)
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return undefined
  // VideoQuality 新格式 → 统一映射函数
  if (raw === '480p' || raw === '720p' || raw === '1080p') return videoQualityToResolution(raw as VideoQuality)
  // 旧格式兼容降级
  if (raw === '1k') return 720
  if (raw === '2k') return 1080
  if (raw.includes('1080')) return 1080
  if (raw.includes('720')) return 720
  if (raw.includes('480')) return 480
  const num = Number(raw.replace(/[^\d.]/g, ''))
  if (Number.isFinite(num) && num > 0) return Math.round(num)
  return undefined
}

/**
 * 执行视频生成任务
 *
 * 管理从任务启动到完成的完整生命周期，包括：
 * - 任务参数规范化
 * - 进度事件通知
 * - 完成结果处理（创建视频图层配置）
 * - 错误处理
 */
export const executeVideoGeneration = (
  generateParams: any,
  extraOptions?: any,
) => {
  const taskEventStore = useTaskEventStore()
  const { getEditor } = useCanvasEditor()
  const userStore = useUserStore()

  if (!_taskManager) {
    console.error('videoTask not initialized')
    return
  }
  const startTask = _taskManager

  let currentClientId = ''
  const modelName =
    (getModuleLabelById(generateParams?.module as any) as string) ||
    'reference_to_video'
  const userTier = resolveGenerationSimulatorUserTier(userStore.highestPackage)
  const {
    userId,
    artworkId,
    uniqueId,
    boardId,
    clientId,
    ...paramsForTask
  } = generateParams || {}
  const normalizedParamsForTask = { ...paramsForTask }
  // MiniMax H3（83-86）的 videoResolution 是 Runninghub 字符串枚举（768P/2K），
  // 必须原样直传，跳过数值化；否则 "768P" 会被 normalizeVideoResolution 解析成 768，
  // 触发 Runninghub errorCode 1007（allowed values: 2K, 768P）
  const isH3VideoTask = isMinimaxStringResolutionModuleId(Number(generateParams.module))
  if (
    !isH3VideoTask &&
    Object.prototype.hasOwnProperty.call(
      normalizedParamsForTask,
      'videoResolution',
    )
  ) {
    const n = normalizeVideoResolution(
      (normalizedParamsForTask as any).videoResolution,
    )
    if (n !== undefined) (normalizedParamsForTask as any).videoResolution = n
  }

  startTask({
    module: generateParams.module,
    userTier,
    params: {
      ...normalizedParamsForTask,
      // 透传预设 clientId（如提示词增强阶段的 enhanceClientId），让真实视频任务与增强阶段占位符共用同一 id，
      // 否则视频服务会生成新 clientId，画布/图库会出现两个"生成中"任务（图片链路 generationEngine 已透传，此处补齐视频链路）
      ...(clientId ? { clientId } : {}),
    },
    onClientId: (newClientId: string) => {
      currentClientId = newClientId
      // 排布来源不得写死为 image_context：底部高级生成框视频依赖 attach._videoTaskSource=bottom_hero_actions；
      // 图库「再次生成」会显式 stamp _generationLayoutSource=bottom_hero。硬编码会导致无操作源时取消插入。
      const startParams = {
        ...normalizedParamsForTask,
        clientId: newClientId,
        canvas_trigger: 'canvas_video',
      }
      const layoutSource = resolveGenerationLayoutSourceKind(startParams)
      taskEventStore.emit('start', {
        clientId: newClientId,
        prompt: generateParams.prompt,
        modelName,
        ratioText: generateParams.videoRatio || '',
        size: 2048,
        params: {
          ...startParams,
          _generationLayoutSource: layoutSource,
        },
      } as any)
      extraOptions?.onClientId?.(newClientId)
    },
    onProgress: (state: any) => {
      bridgeTaskProgressToStore(state, currentClientId)
      extraOptions?.onProgress?.(state)
    },
    onComplete: (resultImages: any) => {
      const { normalizedImages, videoUrl, coverUrl } =
        normalizeVideoCompleteResult(resultImages)

      if (!videoUrl) {
        taskEventStore.emit('failed', {
          clientId: currentClientId,
          status: -1,
          message: '生成结果解析失败',
        } as any)
        extraOptions?.onError?.({ message: '生成结果解析失败' })
        return
      }

      userStore.queryActivityRights()
      const canvasEditor = getEditor()?.fabricCanvas
      const existingVideoCount =
        canvasEditor
          ?.getObjects()
          .filter((o: any) => o?.type === 'video').length || 0

      const finalCoverUrl =
        coverUrl ||
        (normalizedImages.length > 0
          ? normalizedImages[0].image
          : videoUrl)

      taskEventStore.emit('complete', {
        clientId: currentClientId,
        images:
          normalizedImages.length > 0
            ? normalizedImages
            : [{ image: finalCoverUrl, width: 0, height: 0 }],
        extraLayerProps: createVideoLayerConfig({
          videoUrl,
          coverUrl: finalCoverUrl,
          duration: paramsForTask.videoDuration || 5,
          title: `视频${existingVideoCount + 1}`,
        }),
      } as any)
      extraOptions?.onComplete?.({ normalizedImages, videoUrl })
    },
    onError: (errorInfo: any) => {
      const vipChannel = (paramsForTask as any)?.vipChannel as
        | boolean
        | undefined
      // 并发任务冲突按取消处理，此时不再触发外部错误回调（与原有行为一致）
      const cancelled = bridgeTaskErrorToStore(errorInfo, currentClientId, {
        vipChannel,
      })
      if (cancelled) return
      extraOptions?.onError?.(errorInfo)
    },
  })
}

// ========== 来自原 canvasVideoTask.ts ==========

const CANVAS_VIDEO_MODULE_IDS = [
  Number(ModuleId.CanvasStartEndVideo),
  Number(ModuleId.CanvasReferenceVideo),
  Number(ModuleId.CanvasImage2Video),
  Number(ModuleId.SeedanceStartEndVideo),
  Number(ModuleId.SeedanceImage2Video),
  Number(ModuleId.SeedanceReferenceVideo),
  // 文生视频模块(64)：Seedance 2.0 无素材纯文字生成走此通道，重编辑时需识别为画布视频任务
  Number(ModuleId.SeedanceText2Video),
  // Seedance Mini 专属模块（75-78）
  Number(ModuleId.SeedanceMiniStartEndVideo),
  Number(ModuleId.SeedanceMiniReferenceVideo),
  Number(ModuleId.SeedanceMiniImage2Video),
  Number(ModuleId.SeedanceMiniText2Video),
  // MiniMax H3 模块（83-86）
  Number(ModuleId.MinimaxH3StartEndVideo),
  Number(ModuleId.MinimaxH3ReferenceVideo),
  Number(ModuleId.MinimaxH3Image2Video),
  Number(ModuleId.MinimaxH3Text2Video),
  Number(ModuleId.MinimaxH3FastStartEndVideo),
  Number(ModuleId.MinimaxH3FastReferenceVideo),
  Number(ModuleId.MinimaxH3FastImage2Video),
  Number(ModuleId.MinimaxH3FastText2Video),
  Number(ModuleId.MinimaxH3MaxStartEndVideo),
  Number(ModuleId.MinimaxH3MaxImage2Video),
  Number(ModuleId.MinimaxH3MaxText2Video),
]

const VIDEO_SCENE_MODEL_KEYS = [
  'reference_to_video',
  'start_end_to_video',
  'image_to_video',
  'text_to_video',
  'reference_to_video_se',
  'start_end_to_video_se',
  'image_to_video_se',
  'text_to_video_se',
]

const parseAttachObject = (value: unknown): Record<string, any> => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, any>)
        : {}
    } catch {
      return {}
    }
  }
  return value && typeof value === 'object'
    ? (value as Record<string, any>)
    : {}
}

const resolveCanvasVideoModuleId = (value: unknown): number | null => {
  const moduleId = Number(value)
  return CANVAS_VIDEO_MODULE_IDS.includes(moduleId) ? moduleId : null
}

const resolveVideoModelKeyFromParams = (
  params?: Record<string, any> | null,
): VideoModelKey => {
  const rawModuleId = Number(params?.module ?? params?.moduleId ?? params?.modelId)
  // 业务规则：H3（83-86）的 videoModel 是占位值（seedance-2-0），由后端按 module 路由，
  // 必须以 module 为准，否则会被 videoModel 映射误归到 Seedance 2.0。
  if (isMinimaxStringResolutionModuleId(rawModuleId)) {
    return resolveVideoModelKeyByModuleId(rawModuleId)
  }
  const explicitModel = sanitizeUrl(params?.videoModel ?? params?.model).toLowerCase()
  // 精确匹配优先（FR-010），避免 "seedance-mini" 被误判为 "seedance-2-0"
  const mappedKey = mapApiModelToVideoModelKey(explicitModel)
  if (mappedKey) return mappedKey
  return resolveVideoModelKeyByModuleId(rawModuleId)
}

const resolveVideoApiModelFromParams = (
  params: Record<string, any> | null | undefined,
  videoModelKey: VideoModelKey,
): string => {
  const explicitVideoModel = sanitizeUrl(params?.videoModel)
  if (explicitVideoModel) return explicitVideoModel

  const rawModel = sanitizeUrl(params?.model)
  const lowerModel = rawModel.toLowerCase()
  const isSceneModelKey = VIDEO_SCENE_MODEL_KEYS.some((key) => lowerModel.startsWith(key))
  if (rawModel && !isSceneModelKey) return rawModel

  return getVideoApiModelName(videoModelKey)
}

const toPositiveNumber = (value: unknown, fallback = 0): number => {
  const num = Number(value)
  if (Number.isFinite(num) && num > 0) {
    return Math.round(num)
  }
  return fallback
}

export const isCanvasVideoModuleId = (value: unknown): boolean => {
  return resolveCanvasVideoModuleId(value) !== null
}

export const normalizeCanvasVideoQuality = (
  value: unknown,
  fallback = '720p',
): string => {
  const raw = sanitizeUrl(value).toLowerCase()
  if (!raw) return fallback
  if (raw.includes('1080')) return '1080p'
  if (raw.includes('720')) return '720p'
  if (raw.includes('480')) return '480p'
  return raw
}

export const normalizeCanvasVideoResolution = (
  value: unknown,
): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.round(value)
  }
  const raw = sanitizeUrl(value).toLowerCase()
  if (!raw) return undefined
  // VideoQuality 新格式 → 统一映射函数
  if (raw === '480p' || raw === '720p' || raw === '1080p') return videoQualityToResolution(raw as VideoQuality)
  // 旧格式兼容降级
  if (raw === '1k') return 720
  if (raw === '2k') return 1080
  if (raw.includes('1080')) return 1080
  if (raw.includes('720')) return 720
  if (raw.includes('480')) return 480
  const num = Number(raw.replace(/[^\d.]/g, ''))
  return Number.isFinite(num) && num > 0 ? Math.round(num) : undefined
}

export const isCanvasVideoTaskParams = (
  params?: Record<string, any> | null,
): boolean => {
  if (!params) return false
  return (
    resolveCanvasVideoModuleId(
      params.module ?? params.moduleId ?? params.modelId,
    ) !== null
  )
}

export const buildCanvasVideoPanelPayload = (
  params?: Record<string, any> | null,
): CanvasVideoPanelPayload | null => {
  if (!isCanvasVideoTaskParams(params)) return null

  const attachObj = parseAttachObject(params?.attach)
  const mode = resolveCanvasVideoMode(params)
  const videoModelKey = resolveVideoModelKeyFromParams(params)
  const { imageUrls, videoUrls } =
    resolveCanvasVideoReferenceCollections(params)
  const firstFrameUrl = extractOriginalUrl(sanitizeUrl(
    params?.firstFrameUrl ??
    params?.firstImageUrl ??
    params?.originImgUrl ??
    imageUrls[0],
  ))
  const lastFrameUrl = extractOriginalUrl(sanitizeUrl(params?.lastFrameUrl ?? params?.lastImageUrl))
  const selectedVideoUrl = sanitizeUrl(
    params?.selectedVideoUrl ?? params?.videoUrl ?? videoUrls[0],
  )

  return {
    module: params?.module,
    moduleId: params?.moduleId,
    modelId: params?.modelId,
    prompt: sanitizeUrl(params?.prompt),
    duration: toPositiveNumber(
      params?.videoDuration ?? params?.generateSecond ?? params?.duration,
      5,
    ),
    aspectRatio:
      sanitizeUrl(params?.videoRatio ?? params?.aspectRatio) || '',
    quality: normalizeCanvasVideoQuality(
      params?.videoResolution ?? params?.quality,
    ),
    model: resolveVideoApiModelFromParams(params, videoModelKey),
    videoModelKey,
    mode,
    taskSource:
      typeof attachObj._videoTaskSource === 'string'
        ? attachObj._videoTaskSource
        // mergeRecordParamLayers 会把 rawInput.attach 展开到 merged 顶层，而
        // result.param.attach（后端重组）可能整体覆盖 attach 字符串导致标记丢失，故补顶层兜底
        : typeof params?._videoTaskSource === 'string'
          ? params._videoTaskSource
          : undefined,
    firstFrameUrl: firstFrameUrl || undefined,
    lastFrameUrl:
      mode === 'head-tail' && lastFrameUrl ? lastFrameUrl : undefined,
    selectedVideoUrl:
      mode === 'multimodal' && selectedVideoUrl
        ? selectedVideoUrl
        : undefined,
    multimodalImageUrls: mode === 'multimodal' ? imageUrls.map(extractOriginalUrl).filter((url): url is string => Boolean(url)) : [],
    multimodalVideoUrls:
      mode === 'multimodal'
        ? videoUrls.filter((url) => url !== selectedVideoUrl)
        : [],
    movementAmplitude:
      typeof params?.movementAmplitude === 'string'
        ? params.movementAmplitude
        : undefined,
    bgm:
      typeof params?.bgm === 'boolean' ? params.bgm : undefined,
    originImgUrl:
      extractOriginalUrl(sanitizeUrl(params?.originImgUrl ?? firstFrameUrl)) || undefined,
    _videoTargetCid:
      sanitizeUrl(params?._videoTargetCid) || undefined,
    // 透传 Seedance 输入视频总时长，供后端算力计算（仅 Seedance 有视频模式有效）
    inputVideoDuration:
      Number.isFinite(Number(params?.inputVideoDuration)) &&
      Number(params?.inputVideoDuration) > 0
        ? Math.round(Number(params?.inputVideoDuration))
        : undefined,
    // 透传 Seedance 真实计算比例，供后端算力明细计算（图生/首尾帧按图片推断，全能参考同步用户选择）
    calcRatio:
      typeof params?.calcRatio === 'string' && params.calcRatio.length > 0
        ? params.calcRatio
        : undefined,
  }
}

export const isBottomHeroVideoTaskPayload = (
  payload?: CanvasVideoPanelPayload | null,
): boolean => {
  return payload?.taskSource === 'bottom_hero_actions'
}

export const isVideoPanelTaskPayload = (
  payload?: CanvasVideoPanelPayload | null,
  params?: Record<string, any> | null,
): boolean => {
  if (!payload) return false
  if (payload.taskSource === 'video_panel') return true
  if (payload.taskSource === 'bottom_hero_actions') return false

  const candidate = params || {}
  return Boolean(
    payload._videoTargetCid ||
      payload.originImgUrl ||
      candidate._videoTargetCid ||
      candidate.originImgUrl ||
      candidate.firstFrameUrl ||
      candidate.firstImageUrl,
  )
}

export const buildCanvasVideoGenerateParams = (
  params?: Record<string, any> | null,
) => {
  const payload = buildCanvasVideoPanelPayload(params)
  if (!payload) return null

  const isStartEnd = payload.mode === 'head-tail'
  const hasLastFrame = isStartEnd && Boolean(payload.lastFrameUrl)

  const rawModuleId = Number(
    params?.module ?? params?.moduleId ?? params?.modelId,
  )
  const isTextToVideoRecord =
    Number.isFinite(rawModuleId) &&
    (rawModuleId === Number(ModuleId.SeedanceText2Video) ||
     rawModuleId === Number(ModuleId.SeedanceMiniText2Video) ||
     rawModuleId === Number(ModuleId.MinimaxH3Text2Video) ||
     rawModuleId === Number(ModuleId.MinimaxH3FastText2Video) ||
     rawModuleId === Number(ModuleId.MinimaxH3MaxText2Video))

  const imageList =
    payload.mode === 'head-tail'
      ? ([payload.firstFrameUrl, payload.lastFrameUrl].filter(
          Boolean,
        ) as string[])
      : [...payload.multimodalImageUrls]

  if (
    payload.mode === 'multimodal' &&
    payload.firstFrameUrl &&
    !imageList.includes(payload.firstFrameUrl)
  ) {
    imageList.unshift(payload.firstFrameUrl)
  }

  const videoList =
    payload.mode === 'multimodal'
      ? ([
          payload.selectedVideoUrl,
          ...payload.multimodalVideoUrls,
        ].filter(Boolean) as string[])
      : []

  const hasAssets = imageList.length > 0 || videoList.length > 0
  const modelKey = payload.videoModelKey || resolveVideoModelKeyFromParams(params)
  const moduleMode = Number.isFinite(rawModuleId) && rawModuleId > 0
    ? resolveVideoModeByModuleId(rawModuleId)
    : undefined
  const externalMode = payload.mode === 'head-tail'
    ? (moduleMode || (hasLastFrame ? 'start-end' : 'image-to-video'))
    : moduleMode || (modelKey === 'vidu-2-pro' ? 'multimodal' : 'reference')
  const moduleId = isTextToVideoRecord
    ? (rawModuleId === Number(ModuleId.SeedanceMiniText2Video)
      ? ModuleId.SeedanceMiniText2Video
      : rawModuleId === Number(ModuleId.MinimaxH3Text2Video)
        ? ModuleId.MinimaxH3Text2Video
        : rawModuleId === Number(ModuleId.MinimaxH3FastText2Video)
          ? ModuleId.MinimaxH3FastText2Video
          : rawModuleId === Number(ModuleId.MinimaxH3MaxText2Video)
            ? ModuleId.MinimaxH3MaxText2Video
        : ModuleId.SeedanceText2Video)
    : getVideoGenerateModuleIdWithAssets(modelKey, externalMode, hasAssets)

  // H3 / H3 Fast 分辨率档位为字符串 768p/2k，其他模型走数值归一化
  const resolvedVideoResolution = isMinimaxStringResolutionModuleId(rawModuleId)
    ? resolveVideoResolutionParam(rawModuleId, payload.quality as VideoQuality)
    : normalizeCanvasVideoResolution(payload.quality)

  // 文生视频记录无素材时允许提交（不返回 null），构建文生视频参数
  if (isTextToVideoRecord) {
    return {
      module: moduleId,
      imageList: [] as string[],
      videos: undefined,
      prompt: payload.prompt,
      videoModel: payload.model,
      videoMode: payload.mode,
      // H3 文生（86）同样走字符串直传（768P/2K），避免 normalizeCanvasVideoResolution 数值化为 768 触发 Runninghub 1007
      videoResolution: resolvedVideoResolution,
      videoRatio: payload.aspectRatio || undefined,
      videoDuration: payload.duration,
      // 文生视频无参考视频，inputVideoDuration 置空
      inputVideoDuration: undefined,
      // 透传 Seedance 真实计算比例，供后端算力明细
      calcRatio: payload.calcRatio,
      lastFrameUrl: undefined,
      multimodalImageUrls: undefined,
      multimodalVideoUrls: undefined,
      selectedVideoUrl: undefined,
      movementAmplitude: undefined,
      bgm: undefined,
      originImgUrl: undefined,
      _videoTargetCid: payload._videoTargetCid || undefined,
    }
  }

  if (isStartEnd && imageList.length === 0) {
    return null
  }

  if (!isStartEnd && imageList.length === 0 && videoList.length === 0) {
    return null
  }

  return {
    module: moduleId,
    imageList,
    videos: payload.mode === 'multimodal' ? videoList : undefined,
    prompt: payload.prompt,
    videoModel: payload.model,
    videoMode: payload.mode,
    videoResolution: resolvedVideoResolution,
    videoRatio: payload.aspectRatio || undefined,
    videoDuration: payload.duration,
    // 透传 Seedance 输入参考视频总时长，供后端算力计算
    inputVideoDuration: payload.inputVideoDuration,
    // 透传 Seedance 真实计算比例，供后端算力明细
    calcRatio: payload.calcRatio,
    lastFrameUrl: isStartEnd ? payload.lastFrameUrl : undefined,
    multimodalImageUrls:
      payload.mode === 'multimodal'
        ? payload.multimodalImageUrls
        : undefined,
    multimodalVideoUrls:
      payload.mode === 'multimodal'
        ? payload.multimodalVideoUrls
        : undefined,
    selectedVideoUrl:
      payload.mode === 'multimodal'
        ? payload.selectedVideoUrl
        : undefined,
    movementAmplitude: isStartEnd ? payload.movementAmplitude : undefined,
    bgm: isStartEnd ? payload.bgm : undefined,
    originImgUrl:
      payload.originImgUrl || payload.firstFrameUrl || undefined,
    _videoTargetCid: payload._videoTargetCid || undefined,
    ...(isStartEnd
      ? {
          firstImageUrl: payload.firstFrameUrl,
          lastImageUrl: payload.lastFrameUrl || undefined,
        }
      : {}),
  }
}

export { resolveCanvasVideoMode, buildCanvasVideoReferenceItems } from './reference'
export { applyCanvasVideoRegenerateLayoutParams } from './regenerateLayoutParams'
